import { checkPincode, normalizeDigits, getVerifiedPincode, storeVerifiedPincode, clearVerifiedPincode } from '@theme/pincode-gate';

class PincodeChecker extends HTMLElement {
  constructor() {
    super();
    this.onSubmit = this.onSubmit.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onExternalVerification = this.onExternalVerification.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('[data-pincode-form]');
    this.input = this.querySelector('[data-pincode-input]');
    this.message = this.querySelector('[data-pincode-message]');

    if (!this.form || !this.input || !this.message) return;

    this.messages = {
      empty: this.dataset.msgEmpty || 'Please enter pincode.',
      invalid: this.dataset.msgInvalid || 'Please enter a valid 6-digit pincode.',
      success: this.dataset.msgSuccess || 'Delivery is available in your area.',
      unserviceable: this.dataset.msgUnavailable || 'Delivery is not available in your area.',
    };

    // Locked by default (see blocks/buy-buttons.liquid + sticky add-to-cart
    // button in sections/product-information.liquid, both server-render
    // disabled for gated products) - re-assert on connect in case this block
    // was hydrated after the buttons. A pincode already verified elsewhere on
    // the site (product card popup, cart-drawer upsell) unlocks it straight
    // away instead of making the shopper re-type it on the PDP.
    const verifiedPincode = getVerifiedPincode();
    if (verifiedPincode) {
      this.applyVerified(verifiedPincode);
    } else {
      this.setAddToCartEnabled(false);
    }

    this.form.addEventListener('submit', this.onSubmit);
    this.input.addEventListener('input', this.onInput);
    document.addEventListener('pincode-gate:verified', this.onExternalVerification);
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    if (this.input) this.input.removeEventListener('input', this.onInput);
    document.removeEventListener('pincode-gate:verified', this.onExternalVerification);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Fires when the shared pincode-gate state changes anywhere else on the
   * current page - either a pincode gets verified (e.g. the shopper opens
   * the cart drawer from the PDP and verifies one there) so this block
   * unlocks without a page reload, or a *different* one fails and clears the
   * earlier success (see onSubmit below), so this block re-locks too instead
   * of keeping the shopper unlocked on stale state.
   */
  onExternalVerification() {
    const verifiedPincode = getVerifiedPincode();
    if (verifiedPincode) {
      this.applyVerified(verifiedPincode);
    } else {
      this.clearMessage();
      this.input.removeAttribute('aria-invalid');
      this.setAddToCartEnabled(false);
    }
  }

  /**
   * A PDP can be restored from the browser's back/forward cache instead of a
   * real reload (e.g. the shopper checks a pincode elsewhere, then hits Back
   * to return here) - connectedCallback doesn't re-run on that restore, so
   * without this the button would stay locked even though localStorage now
   * has a verified pincode.
   */
  onPageShow(event) {
    if (event.persisted) this.onExternalVerification();
  }

  /** @param {string} pincode */
  applyVerified(pincode) {
    this.input.value = pincode;
    this.input.removeAttribute('aria-invalid');
    this.setMessage('success', this.messages.success);
    this.setAddToCartEnabled(true);
  }

  onInput() {
    const digits = normalizeDigits(this.input.value);
    if (digits !== this.input.value) this.input.value = digits;

    if (this.message.textContent) {
      this.clearMessage();
      this.input.removeAttribute('aria-invalid');
    }
  }

  onSubmit(event) {
    event.preventDefault();

    const result = checkPincode(this.input.value);

    if (!result.ok) {
      // Clear and broadcast BEFORE setting this instance's own error state.
      // dispatchEvent runs synchronously, which re-enters this same
      // component's onExternalVerification listener (registered below) right
      // here - it sees no verified pincode and calls clearMessage(). Doing
      // that first means the "real" setMessage/setAddToCartEnabled calls
      // below run last and are what actually stick, instead of being wiped
      // out immediately by this component reacting to its own broadcast.
      if (result.reason !== 'empty') {
        clearVerifiedPincode();
        document.dispatchEvent(new CustomEvent('pincode-gate:verified', { detail: { pincode: null } }));
      }

      this.setMessage('error', this.messages[result.reason ?? 'invalid']);
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
      this.setAddToCartEnabled(false);
      return;
    }

    this.setMessage('success', this.messages.success);
    this.input.removeAttribute('aria-invalid');
    this.setAddToCartEnabled(true);

    // Share the verified pincode with the rest of the site (product card
    // popup, cart-drawer upsell) the same way their "Check Availability"
    // popup already does, so checking it here also skips the gate elsewhere.
    const pincode = this.input.value.trim();
    storeVerifiedPincode(pincode);
    document.dispatchEvent(new CustomEvent('pincode-gate:verified', { detail: { pincode } }));
  }

  /**
   * Enables/disables the real PDP add-to-cart button (main buy-buttons block
   * and the sticky bar) for this product.
   *
   * @param {boolean} enabled
   */
  setAddToCartEnabled(enabled) {
    const productId = this.dataset.productId;
    if (!productId) return;

    const section = this.closest('.shopify-section');

    const addToCartComponent = section?.querySelector(
      `product-form-component[data-product-id="${productId}"] add-to-cart-component`
    );
    if (addToCartComponent) {
      if (enabled) addToCartComponent.enable?.();
      else addToCartComponent.disable?.();
    }

    const stickyButton = document.querySelector(
      `sticky-add-to-cart[data-product-id="${productId}"] [ref="addToCartButton"]`
    );
    if (stickyButton) stickyButton.disabled = !enabled;
  }

  setMessage(state, text) {
    this.message.textContent = text;
    this.message.setAttribute('data-state', state);
  }

  clearMessage() {
    this.message.textContent = '';
    this.message.removeAttribute('data-state');
  }
}

if (!customElements.get('pincode-checker')) {
  customElements.define('pincode-checker', PincodeChecker);
}
